import { useEffect, useState } from 'react'
import { trackEvent } from '../../../utils/appBridge'
import BottomSheetContent from '../BottomSheetContent'
import { EventNames } from '../../../types/misc.types'
import { InvestPaymentMethodType } from '../../../types/mf-execution.types'
import RenderHTML from '../../ui-components/RenderHTML'
import { PrimaryButton } from '../../execution/PrimaryButton'
import { mfLogoBaseUrl } from '../../../env'

export function SelectBankAccountBottomSheet({
  onDismiss,
  bankAccounts,
  selectedBankAccount,
  onConfirm,
  ScreenName,
}: {
  onDismiss: () => void
  bankAccounts: InvestPaymentMethodType[]
  selectedBankAccount: InvestPaymentMethodType | null
  onConfirm: (bankAccount: InvestPaymentMethodType) => void
  ScreenName: string
}) {
  const [pendingBankAccount, setPendingBankAccount] = useState<InvestPaymentMethodType | null>(
    () => selectedBankAccount ?? bankAccounts[0] ?? null
  )

  useEffect(() => {
    trackEvent({
      eventName: EventNames.KnowMoreBottomSheetLoad,
      payload: {
        eventId: 'Select bank account',
        ScreenName,
      },
    })
  }, [ScreenName])

  const handleConfirm = () => {
    if (pendingBankAccount) {
      onConfirm(pendingBankAccount)
    }
    onDismiss()
  }

  return (
    <BottomSheetContent
      heading='Select bank account'
      bottomSheetHeadingClassName='!text-[18px] !font-[500] !mb-0'
      bottomSheetBodyClassName='!px-0 !pb-0'
      onDismiss={onDismiss}
      bottomSheetCtas={
        <PrimaryButton
          onClick={handleConfirm}
          label='Confirm'
          isDisabled={!pendingBankAccount}
          primaryButtonClassName='!h-[56px] !text-[16px] !font-[500]'
        />
      }
    >
      <div className='flex flex-col'>
        <ul className='flex flex-col'>
          {bankAccounts?.map((bankAccount, index) => {
            const isSelected =
              pendingBankAccount?.paymentMethodId != null &&
              pendingBankAccount.paymentMethodId === bankAccount?.paymentMethodId

            return (
              <li key={`${bankAccount?.paymentMethodId ?? bankAccount?.displayAccNumber}-${index}`}>
                <div
                  className='flex items-center justify-between w-full px-5 py-5 cursor-pointer'
                  onClick={() => setPendingBankAccount(bankAccount)}
                >
                  <div className='flex items-center gap-5 min-w-0 flex-1'>
                    <img
                      src={getSelectBankLogoSrc(bankAccount?.logoUrl)}
                      alt={bankAccount?.displayAccNumber ?? ''}
                      className='w-10 h-10 rounded-lg object-contain bg-grey80 flex-shrink-0'
                      onError={(event) => {
                        event.currentTarget.src = '/images/mutual_fund/categories/banks_grayscale.svg'
                      }}
                    />
                    <p className='text-sm font-medium text-grey10 truncate'>
                      {bankAccount?.displayAccNumber ?? ''}
                    </p>
                  </div>
                  <div className='flex-shrink-0 ml-3'>
                    <RenderHTML innerHTMLText={isSelected ? bankAccountCheckedIcon : bankAccountUncheckedIcon} />
                  </div>
                </div>
                {index < bankAccounts.length - 1 ? <div className='mx-5 h-px bg-grey60' /> : null}
              </li>
            )
          })}
        </ul>
        <p className='text-xs text-grey40 px-5 pt-2 pb-4'>
          To add or delete a bank, go to Profile &gt; Banks &amp; Autopay
        </p>
      </div>
    </BottomSheetContent>
  )
}

function getSelectBankLogoSrc(logoPath?: string) {
  if (!logoPath) {
    return '/images/mutual_fund/categories/banks_grayscale.svg'
  }

  return logoPath.startsWith('http') ? logoPath : `${mfLogoBaseUrl}${logoPath}`
}

const bankAccountUncheckedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <circle cx="10" cy="10" r="9" stroke="#D1D6D6" stroke-width="2"/>
</svg>`

const bankAccountCheckedIcon = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
  <path d="M10 0C8.02219 0 6.08879 0.58649 4.4443 1.6853C2.79981 2.78412 1.51809 4.3459 0.761209 6.17317C0.00433284 8.00043 -0.193701 10.0111 0.192152 11.9509C0.578004 13.8907 1.53041 15.6725 2.92894 17.0711C4.32746 18.4696 6.10929 19.422 8.0491 19.8079C9.98891 20.1937 11.9996 19.9957 13.8268 19.2388C15.6541 18.4819 17.2159 17.2002 18.3147 15.5557C19.4135 13.9112 20 11.9778 20 10C19.9968 7.34881 18.9422 4.80712 17.0676 2.93244C15.1929 1.05776 12.6512 0.00317572 10 0V0ZM14.884 8.384L9.384 13.884C9.26793 14.0001 9.13011 14.0922 8.97844 14.1551C8.82676 14.2179 8.66418 14.2502 8.5 14.2502C8.33582 14.2502 8.17325 14.2179 8.02157 14.1551C7.8699 14.0922 7.73208 14.0001 7.616 13.884L5.116 11.384C4.88155 11.1495 4.74984 10.8316 4.74984 10.5C4.74984 10.1684 4.88155 9.85045 5.116 9.616C5.35046 9.38155 5.66844 9.24984 6 9.24984C6.33157 9.24984 6.64955 9.38155 6.884 9.616L8.5 11.232L13.116 6.616C13.2321 6.49991 13.3699 6.40782 13.5216 6.345C13.6733 6.28217 13.8358 6.24984 14 6.24984C14.1642 6.24984 14.3267 6.28217 14.4784 6.345C14.6301 6.40782 14.7679 6.49991 14.884 6.616C15.0001 6.73209 15.0922 6.86991 15.155 7.02158C15.2178 7.17326 15.2502 7.33583 15.2502 7.5C15.2502 7.66417 15.2178 7.82674 15.155 7.97842C15.0922 8.13009 15.0001 8.26791 14.884 8.384Z" fill="#232323"/>
</svg>`
